//frontend/src/components/dashboard/ClientsManagement.js
import React, { useState, useEffect, useRef } from 'react';
import {
 Box, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
 Typography, Button, IconButton, Tooltip, Link, Chip, Tabs, Tab, Menu, MenuItem
} from '@mui/material';
import {
 Phone, WhatsApp, Telegram, Instagram, Edit, History, Delete,
 AssignmentInd, ArrowDropDown, Settings as SettingsIcon
} from '@mui/icons-material';
import ClientDetailsDialog from './ClientDetailsDialog';
import ClientHistoryDialog from './ClientHistoryDialog';
import AssignSpecialistDialog from './AssignSpecialistDialog';
import TabsSettings from './TabsSettings';
import axiosInstance from '../../utils/axios';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';

const getStatusColor = (status) => {
 switch (status) {
   case 'completed':
   case 'development':
     return {
       color: 'success.main',
       borderColor: 'success.main',
       bgcolor: 'success.lighter'
     };
   case 'unreachable':
     return {
       color: 'error.main',
       borderColor: 'error.main',
       bgcolor: 'error.lighter'
     };
   case 'negotiating':
   case 'meeting':
   case 'discussion':
   case 'contacted':
     return {
       color: 'warning.main',
       borderColor: 'warning.main',
       bgcolor: 'warning.lighter'
     };
   default:
     return {};
 }
};

const TabsWithDropdown = ({ tabs, value, onChange }) => {
 const [anchorEl, setAnchorEl] = useState(null);
 const [useDropdown, setUseDropdown] = useState(false);
 const tabsContainerRef = useRef(null);

 useEffect(() => {
   const checkWidth = () => {
     if (tabsContainerRef.current) {
       const containerWidth = tabsContainerRef.current.offsetWidth;
       const tabsWidth = tabs.length * 150;
       setUseDropdown(tabsWidth > containerWidth);
     }
   };
   checkWidth();
   window.addEventListener('resize', checkWidth);
   return () => window.removeEventListener('resize', checkWidth);
 }, [tabs]);

 return (
   <Box ref={tabsContainerRef} sx={{ width: '100%', mb: 3 }}>
     {useDropdown ? (
       <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
         <Button
           onClick={(e) => setAnchorEl(e.currentTarget)}
           endIcon={<ArrowDropDown />}
           sx={{
             py: 1.5,
             textTransform: 'none',
             color: 'text.primary',
             borderRadius: 0,
             borderBottom: 2,
             borderColor: 'primary.main'
           }}
         >
           {tabs[value]?.label}
         </Button>
         <Menu
           anchorEl={anchorEl}
           open={Boolean(anchorEl)}
           onClose={() => setAnchorEl(null)}
         >
           {tabs.map((tab, index) => (
             <MenuItem 
               key={tab.key || index}
               onClick={() => {
                 onChange(index);
                 setAnchorEl(null);
               }}
               selected={index === value}
             >
               {tab.label}
             </MenuItem>
           ))}
         </Menu>
       </Box>
     ) : (
       <Tabs 
         value={value} 
         onChange={(e, newValue) => onChange(newValue)}
         sx={{ borderBottom: 1, borderColor: 'divider' }}
       >
         {tabs.map((tab, index) => (
           <Tab 
             key={tab.key || index} 
             label={tab.label}
             sx={{ textTransform: 'none' }}
           />
         ))}
       </Tabs>
     )}
   </Box>
 );
};

export default function ClientsManagement({ isSuperAdmin, specialistId, user }) {
    const { t } = useTranslation();
 const [clients, setClients] = useState([]);
 const [deletedClients, setDeletedClients] = useState([]);
 const [selectedClient, setSelectedClient] = useState(null);
 const [openDetails, setOpenDetails] = useState(false);
 const [openHistory, setOpenHistory] = useState(false);
 const [openAssignDialog, setOpenAssignDialog] = useState(false);
 const [openSettings, setOpenSettings] = useState(false);
 const [tabValue, setTabValue] = useState(0);
 const [settings, setSettings] = useState({
   columnSettings: {
     showNameCompany: true,
     showFirstContact: true,
     showContacts: true,
     showCreatedBy: true,
     showAssignedTo: true,
     showStatus: true,
     showProjectType: true
   }
 });

 useEffect(() => {
   fetchClients();
   fetchSettings();
 }, [specialistId]);

 const fetchSettings = async () => {
   try {
     const response = await axiosInstance.get('/settings/tabs');
     if (response.data.columnSettings) {
       setSettings({ columnSettings: response.data.columnSettings });
     }
   } catch (error) {
     console.error('Error:', error);
   }
 };

 const fetchClients = async () => {
   try {
     const response = await axiosInstance.get('/clients');
     setClients(response.data);
     if (isSuperAdmin) {
       const deletedResponse = await axiosInstance.get('/clients/deleted');
       setDeletedClients(deletedResponse.data);
     }
   } catch (error) {
     console.error('Error:', error);
   }
 };

 const getTranslatedStatus = (status) => t(`common.projectStatus.${status}`);

 const handleMoveToDeleted = async (clientId) => {
   try {
     await axiosInstance.patch(`/clients/${clientId}/move-to-deleted`);
     await fetchClients();
   } catch (error) {
     console.error('Error:', error);
   }
 };

 const handleDeletePermanently = async (clientId) => {
   try {
     await axiosInstance.delete(`/clients/${clientId}`);
     fetchClients();
   } catch (error) {
     console.error('Error:', error);
   }
 };

 const handleClearAllDeleted = async () => {
   if (window.confirm(t('common.confirmClearAllDeleted'))) {
     try {
       await axiosInstance.delete('/clients/clear-deleted');
       await fetchClients();
     } catch (error) {
       console.error('Error:', error);
     }
   }
 };

 const handleAssignSpecialists = async (clientId, { specialistIds, isVisibleToAll }) => {
   try {
     await axiosInstance.patch(`/clients/${clientId}/assign-specialists`, {
       specialistIds,
       isVisibleToAll
     });
     fetchClients();
   } catch (error) {
     console.error('Error:', error);
   }
 };

 const getFilteredClients = (status) => {
   return clients.filter(client => !client.isDeleted && client.status === status);
 };

 const renderClientsList = (clientsList, isDeletedTab = false) => (
   <TableContainer component={Paper}>
     <Table>
       <TableHead>
         <TableRow>
           {settings.columnSettings.showNameCompany && 
             <TableCell>{t('common.namecompany')}</TableCell>}
           {settings.columnSettings.showFirstContact && 
             <TableCell>{t('common.firstcontact')}</TableCell>}
           {settings.columnSettings.showContacts && 
             <TableCell>{t('common.contacts')}</TableCell>}
           {settings.columnSettings.showCreatedBy && 
             <TableCell>{t('common.createdby')}</TableCell>}
           {settings.columnSettings.showAssignedTo && isSuperAdmin && 
             <TableCell>{t('common.assignedto')}</TableCell>}
           <TableCell>{t('common.actions')}</TableCell>
         </TableRow>
       </TableHead>
       <TableBody>
         {clientsList.map((client) => (
           <TableRow key={client._id}>
             {settings.columnSettings.showNameCompany && (
               <TableCell>
               <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                   {settings.columnSettings.showStatus && (
                       <Chip 
                           label={getTranslatedStatus(client.projectStatus)}
                           variant="outlined"
                           size="small"
                           sx={{ 
                               alignSelf: 'flex-start',
                               mb: 0.5,
                               ...getStatusColor(client.projectStatus)
                           }}
                       />
                   )}
                   {settings.columnSettings.showProjectType && client.projectType && (
                       <Chip
                           label={client.projectType}
                           variant="outlined"
                           size="small"
                           sx={{ 
                               alignSelf: 'flex-start',
                               mb: 0.5,
                               ...getStatusColor(client.projectStatus)
                           }}
                       />
                   )}
                   <Typography variant="subtitle2">{client.name}</Typography>
                   {client.website && (
                       <Link 
                           href={client.website.startsWith('http') ? client.website : `https://${client.website}`} 
                           target="_blank" 
                           variant="body2"
                       >
                           {client.website.replace(/^https?:\/\//, '')}
                       </Link>
                   )}
               </Box>
           </TableCell>
             )}
             {settings.columnSettings.showFirstContact && (
               <TableCell>
                 {format(new Date(client.firstContact), 'dd.MM.yyyy')}
               </TableCell>
             )}
             {settings.columnSettings.showContacts && (
               <TableCell>
                 <Box sx={{ display: 'flex', gap: 1 }}>
                   {client.phone && (
                     <Tooltip title={client.phone}>
                       <IconButton size="small" href={`tel:${client.phone}`}>
                         <Phone fontSize="small" />
                       </IconButton>
                     </Tooltip>
                   )}
                   {client.whatsapp && (
                     <Tooltip title="WhatsApp">
                       <IconButton 
                         size="small" 
                         href={`https://wa.me/${client.whatsapp}`}
                         target="_blank"
                       >
                         <WhatsApp fontSize="small" />
                       </IconButton>
                     </Tooltip>
                   )}
                   {client.telegram && (
                     <Tooltip title="Telegram">
                       <IconButton size="small" href={`https://t.me/${client.telegram}`} target="_blank">
                         <Telegram fontSize="small" />
                       </IconButton>
                     </Tooltip>
                   )}
                   {client.instagram && (
                     <Tooltip title="Instagram">
                       <IconButton size="small" href={`https://instagram.com/${client.instagram}`} target="_blank">
                         <Instagram fontSize="small" />
                       </IconButton>
                     </Tooltip>
                   )}
                 </Box>
               </TableCell>
             )}
             {settings.columnSettings.showCreatedBy && (
               <TableCell>{client.createdBy?.name || 'N/A'}</TableCell>
             )}
             {settings.columnSettings.showAssignedTo && isSuperAdmin && (
               <TableCell>
                 {client.isVisibleToAll ? (
                   <Chip size="small" label={t('common.allspecialists')} />
                 ) : (
                   client.specialistId?.map(specialist => (
                     <Chip 
                       key={specialist._id}
                       size="small"
                       label={specialist.name}
                       sx={{ mr: 0.5 }}
                     />
                   ))
                 )}
               </TableCell>
             )}
             <TableCell>
               <Box sx={{ display: 'flex', gap: 1 }}>
                 <Tooltip title={t('common.edit')}>
                   <IconButton 
                     size="small"
                     onClick={() => {
                       setSelectedClient(client);
                       setOpenDetails(true);
                     }}
                   >
                     <Edit fontSize="small" />
                   </IconButton>
                 </Tooltip>
                 <Tooltip title={t('common.history')}>
                   <IconButton
                     size="small"
                     onClick={() => {
                       setSelectedClient(client);
                       setOpenHistory(true);
                     }}
                   >
                     <History fontSize="small" />
                   </IconButton>
                 </Tooltip>
                 {isSuperAdmin && !isDeletedTab && (
                   <Tooltip title={t('common.assignSpecialists')}>
                     <IconButton
                       size="small"
                       onClick={() => {
                         setSelectedClient(client);
                         setOpenAssignDialog(true);
                       }}
                     >
                       <AssignmentInd fontSize="small" />
                     </IconButton>
                   </Tooltip>
                 )}
                 <Tooltip title={isDeletedTab ? t('common.delete') : t('common.delete')}>
                   <IconButton
                     size="small"
                     color="error"
                     onClick={() => isDeletedTab ? 
                       handleDeletePermanently(client._id) : 
                       handleMoveToDeleted(client._id)
                     }
                   >
                     <Delete fontSize="small" />
                   </IconButton>
                 </Tooltip>
               </Box>
             </TableCell>
           </TableRow>
         ))}
       </TableBody>
     </Table>
   </TableContainer>
 );

 const tabs = [
   { key: 'new', label: t('common.new') },
   { key: 'regular', label: t('common.regular') },
   { key: 'former', label: t('common.former') },
   { key: 'deleted', label: t('common.deleted') }
 ];

 return (
   <Box>
     <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
       <Typography variant="h6">
         {t('dashboard.clients.management')}
       </Typography>
       <Box>
         <Button 
           variant="outlined"
           startIcon={<SettingsIcon />}
           onClick={() => setOpenSettings(true)}
           sx={{ mr: 2 }}
         >
           {t('common.settings')}
         </Button>
         <Button 
           variant="contained"
           onClick={() => {
             setSelectedClient(null);
             setOpenDetails(true);
           }}
         >
           {t('common.addnewclient')}
         </Button>
       </Box>
     </Box>

     <TabsWithDropdown
       tabs={tabs}
       value={tabValue}
       onChange={setTabValue}
     />

     {tabValue === 3 && isSuperAdmin && (
       <Box sx={{ mb: 2 }}>
         <Button 
           variant="contained" 
           color="error" 
           onClick={handleClearAllDeleted}
         >
           {t('common.clearalldeleted')}
         </Button>
       </Box>
     )}

{tabValue === 0 && renderClientsList(getFilteredClients('new'))}
     {tabValue === 1 && renderClientsList(getFilteredClients('regular'))}
     {tabValue === 2 && renderClientsList(getFilteredClients('former'))}
     {tabValue === 3 && renderClientsList(isSuperAdmin ? deletedClients : clients.filter(c => c.isDeleted), true)}

     <ClientDetailsDialog 
       open={openDetails}
       onClose={() => setOpenDetails(false)}
       client={selectedClient}
       onSave={fetchClients}
       specialistId={specialistId}
     />

     <ClientHistoryDialog
       open={openHistory}
       onClose={() => setOpenHistory(false)}
       client={selectedClient}
     />

     {isSuperAdmin && (
       <AssignSpecialistDialog
         open={openAssignDialog}
         onClose={() => setOpenAssignDialog(false)}
         client={selectedClient}
         onSave={handleAssignSpecialists}
       />
     )}

<TabsSettings
    open={openSettings}
    onClose={() => setOpenSettings(false)}
    isAdmin={['admin', 'superadmin'].includes(user?.role)}
  />
   </Box>
 );
}